/**
 * TODO 6.4 — Step3Team.jsx
 * Tela 3 — Equipe de Profissionais (com loop)
 * 
 * Props: wizard (useWelcomeWizard)
 * Barra n=3
 * - Título temático com firstName
 * - Dois botões: "Só eu mesmo" | "Sim, tenho uma equipe"
 * - "Só eu": cria Profissional 0 = userName; rebuildSteps() + goNext()
 * - "Sim": loop de adição com chips, Prof 0 protegido, rebuildSteps() ao confirmar
 */

import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { getWelcomeText } from '../../../utils/welcomeText';
import { wlog } from '../../../utils/welcomeDebug';

export default function Step3Team({ wizard }) {
  wlog('Step3Team', 'RENDER', { templateId: wizard.template?.id || 'NENHUM' });
  const [mode, setMode] = useState(null); // null | 'solo' | 'team'
  const [professionals, setProfessionals] = useState([]);
  const [newMemberName, setNewMemberName] = useState('');
  // Extrai firstName do userName
  const firstName = wizard.state?.userName?.split(' ')[0]?.trim() || '';
  const userName = wizard.state?.userName || '';

  // Texto temático — getWelcomeText é síncrono, sem estado de "loading".
  const titleText =
    getWelcomeText(wizard.template, 'teamQuestion', { firstName }) ||
    `${firstName}, além de você, mais alguém atende aí?`;

  // Inicializa o array de profissionais ao montar (sempre começa com Prof 0 = usuário)
  useEffect(() => {
    if (wizard.state?.userName) {
      setProfessionals([
        {
          id: 0,
          name: wizard.state.userName,
        },
      ]);
    }
  }, [wizard.state?.userName]);

  // Seleciona modo "Só eu"
  const handleSolo = () => {
    const prof0 = { id: 0, name: userName };
    wizard.setProfessionals([prof0]);
    wizard.rebuildSteps();
    wizard.goNext();
  };

  // Seleciona modo "Sim, tenho equipe"
  const handleTeam = () => {
    setMode('team');
  };

  // Adiciona um novo membro
  const handleAddMember = () => {
    if (!newMemberName.trim()) return;

    const newProf = {
      id: professionals.length, // id sequencial
      name: newMemberName.trim(),
    };

    setProfessionals([...professionals, newProf]);
    setNewMemberName('');
  };

  // Remove um membro (exceto Prof 0)
  const handleRemoveMember = (id) => {
    if (id === 0) return; // Prof 0 não pode ser removido

    setProfessionals(professionals.filter((p) => p.id !== id));
  };

  // Confirma a equipe e avança
  const handleConfirmTeam = () => {
    wizard.setProfessionals(professionals);
    wizard.rebuildSteps();
    wizard.goNext();
  };

  // --- Tela inicial: dois botões ---
  if (mode === null) {
    return (
      <Box
        component="section"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px 40px',
          minHeight: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            maxWidth: '500px',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {/* Título temático */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 600,
              marginBottom: '40px',
              color: 'text.primary',
            }}
          >
            {titleText}
          </Typography>

          {/* Dois botões principais */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Button
              variant="contained"
              size="large"
              onClick={handleSolo}
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                paddingY: '14px',
              }}
            >
              🙋 Só eu mesmo
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={handleTeam}
              sx={{
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 600,
                paddingY: '14px',
              }}
            >
              👥 Sim, tenho uma equipe
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  // --- Tela de adição de profissionais (modo === 'team') ---
  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '60px 20px 40px',
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          maxWidth: '500px',
          width: '100%',
        }}
      >
        {/* Título */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            marginBottom: '8px',
            color: '#333',
          }}
        >
          Sua equipe:
        </Typography>

        {/* Lista de profissionais com chips */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            justifyContent: 'center',
            marginBottom: '32px',
            padding: '16px',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            minHeight: '60px',
            alignItems: 'center',
          }}
        >
          {professionals.map((prof) => (
            <Chip
              key={prof.id}
              label={prof.name}
              onDelete={prof.id !== 0 ? () => handleRemoveMember(prof.id) : undefined}
              deleteIcon={prof.id !== 0 ? <Close /> : undefined}
              sx={{
                fontWeight: prof.id === 0 ? 600 : 400,
                backgroundColor: prof.id === 0 ? '#e3f2fd' : '#fff',
                border: prof.id === 0 ? '2px solid #2196f3' : '1px solid #ddd',
              }}
            />
          ))}
        </Box>

        {/* Campo de adição */}
        <Typography
          sx={{
            fontSize: '13px',
            color: '#666',
            marginBottom: '12px',
            fontWeight: 500,
          }}
        >
          Mais alguém na equipe?
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            marginBottom: '32px',
          }}
        >
          <TextField
            fullWidth
            value={newMemberName}
            onChange={(e) => setNewMemberName(e.target.value)}
            placeholder="Nome do profissional"
            variant="outlined"
            size="small"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleAddMember();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '14px',
              },
            }}
          />

          <Button
            variant="outlined"
            onClick={handleAddMember}
            disabled={!newMemberName.trim()}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            Adicionar
          </Button>
        </Box>

        {/* Botão de confirmação — adicionar membros é pelo campo acima.
            Este botão finaliza a equipe e avança. */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleConfirmTeam}
          sx={{
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 600,
            paddingY: '12px',
          }}
        >
          {professionals.length > 1
            ? `Pronto, é essa minha equipe (${professionals.length})`
            : 'Continuar'}
        </Button>

        {/* Voltar ao menu inicial */}
        <Button
          size="small"
          onClick={() => setMode(null)}
          sx={{
            marginTop: '24px',
            width: '100%',
            textTransform: 'none',
            color: '#999',
          }}
        >
          ← Voltar
        </Button>
      </Box>
    </Box>
  );
}
